import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Bell, Mail, Settings, RefreshCw, ArrowLeft, FileDown, ArrowUpDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from './config';

function AlarmSayfasi({ user }) {
    const [alarmData, setAlarmData] = useState({ toplam: 0, gecikmis: 0, yaklasan: 0, alarmlar: [], esik_gun: 30 });
    const [ayarlar, setAyarlar] = useState({ esik_gun: 30, email_bildirimi_aktif: true });
    const [showSettings, setShowSettings] = useState(false);
    const [loading, setLoading] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [firmalar, setFirmalar] = useState([]);
    const [selectedFirma, setSelectedFirma] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const isAdmin = user?.rol === 'Admin';
    const isMusteriOnly = user?.rol === 'Müşteri';

    const fetchAlarmlar = useCallback(async () => {
        setLoading(true);
        try {
            const firmaId = isMusteriOnly ? user.firma_id : (selectedFirma || 0);
            const res = await api.get('/api/alarmlar', { params: { firma_id: firmaId } });
            setAlarmData(res.data);
        } catch (err) {
            console.error('Alarm verisi alınamadı:', err);
        }
        setLoading(false);
    }, [isMusteriOnly, user?.firma_id, selectedFirma]);

    useEffect(() => {
        if (isAdmin) {
            api.get('/api/firmalar').then(res => setFirmalar(res.data));
            api.get('/api/ayarlar').then(res => setAyarlar(res.data));
        }
        fetchAlarmlar();
    }, [isAdmin, fetchAlarmlar]);

    useEffect(() => {
        fetchAlarmlar();
    }, [selectedFirma, fetchAlarmlar]);

    const updateAyarlar = async () => {
        try {
            await api.put('/api/ayarlar', ayarlar);
            alert('Ayarlar güncellendi!');
            fetchAlarmlar();
        } catch (err) {
            alert('Ayarlar güncellenemedi!');
        }
    };

    const sendEmails = async () => {
        if (!window.confirm('Tüm firmalara alarm email\'i gönderilsin mi?')) return;
        setEmailSending(true);
        try {
            const res = await api.post('/api/alarmlar/email-gonder');
            alert(res.data.mesaj);
        } catch (err) {
            alert('Email gönderilemedi!');
        }
        setEmailSending(false);
    };

    const downloadPDF = async () => {
        try {
            const firmaId = isMusteriOnly ? user.firma_id : (selectedFirma || 0);
            const res = await api.get(`/api/rapor/alarmlar?firma_id=${firmaId}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `alarmlar_rapor_${new Date().toISOString().split('T')[0]}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert('PDF indirme başarısız: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedAlarmlar = [...(alarmData.alarmlar || [])].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const SortableHeader = ({ label, sortKey }) => (
        <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(sortKey)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {label}
                <ArrowUpDown size={14} color={sortConfig.key === sortKey ? '#3b82f6' : '#9ca3af'} />
            </div>
        </th>
    );

    return (
        <div style={{ padding: '20px', backgroundColor: '#f3f4f6', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', color: '#3b82f6', textDecoration: 'none' }}>
                    <ArrowLeft size={18} /> Ana Sayfa
                </Link>

                <div style={panelStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Bell size={28} color="#f59e0b" />
                            Bakım Alarmları
                        </h2>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {isAdmin && (
                                <select
                                    value={selectedFirma}
                                    onChange={e => setSelectedFirma(e.target.value)}
                                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                                >
                                    <option value="">Tüm Firmalar</option>
                                    {firmalar.map(f => <option key={f.firma_id} value={f.firma_id}>{f.firma_adi}</option>)}
                                </select>
                            )}
                            <button onClick={fetchAlarmlar} style={iconBtnStyle} title="Yenile">
                                <RefreshCw size={18} className={loading ? 'spin' : ''} />
                            </button>
                            {isAdmin && (
                                <>
                                    <button onClick={() => setShowSettings(!showSettings)} style={iconBtnStyle} title="Ayarlar">
                                        <Settings size={18} />
                                    </button>
                                    <button onClick={sendEmails} disabled={emailSending} style={{ ...btnStyle, backgroundColor: '#3b82f6' }} title="Email Gönder">
                                        <Mail size={16} /> {emailSending ? '...' : 'Email Gönder'}
                                    </button>
                                </>
                            )}
                            <button onClick={downloadPDF} style={{ ...btnStyle, backgroundColor: '#10b981' }} title="PDF İndir">
                                <FileDown size={16} /> PDF
                            </button>
                        </div>
                    </div>

                    {/* Özet Kartları */}
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                        <div style={{ ...summaryCard, borderColor: '#ef4444' }}>
                            <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#ef4444' }}>{alarmData.gecikmis}</span>
                            <span style={{ color: '#6b7280', fontSize: '13px' }}>Gecikmiş</span>
                        </div>
                        <div style={{ ...summaryCard, borderColor: '#f59e0b' }}>
                            <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>{alarmData.yaklasan}</span>
                            <span style={{ color: '#6b7280', fontSize: '13px' }}>Yaklaşan ({alarmData.esik_gun} gün)</span>
                        </div>
                        <div style={{ ...summaryCard, borderColor: '#10b981' }}>
                            <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>{alarmData.toplam}</span>
                            <span style={{ color: '#6b7280', fontSize: '13px' }}>Toplam</span>
                        </div>
                    </div>

                    {/* Ayarlar Paneli */}
                    {showSettings && isAdmin && (
                        <div style={{ padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', marginBottom: '15px' }}>
                            <h4 style={{ margin: '0 0 10px' }}>⚙️ Alarm Ayarları</h4>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div>
                                    <label style={labelStyle}>Eşik Gün Sayısı</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={ayarlar.esik_gun}
                                        onChange={e => setAyarlar({ ...ayarlar, esik_gun: parseInt(e.target.value) || 30 })}
                                        style={{ ...inputStyle, width: '80px' }}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Email Bildirimi</label>
                                    <select
                                        value={ayarlar.email_bildirimi_aktif ? 'true' : 'false'}
                                        onChange={e => setAyarlar({ ...ayarlar, email_bildirimi_aktif: e.target.value === 'true' })}
                                        style={inputStyle}
                                    >
                                        <option value="true">Aktif</option>
                                        <option value="false">Pasif</option>
                                    </select>
                                </div>
                                <button onClick={updateAyarlar} style={{ ...btnStyle, backgroundColor: '#10b981', marginTop: '20px' }}>
                                    Kaydet
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Alarm Listesi */}
                    {alarmData.alarmlar.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f3f4f6', textAlign: 'left' }}>
                                        <SortableHeader label="Firma" sortKey="firma_adi" />
                                        <SortableHeader label="Seri No" sortKey="seri_no" />
                                        <SortableHeader label="Konum" sortKey="konum" />
                                        <SortableHeader label="Bakım Tarihi" sortKey="bir_sonraki_bakim_tarihi" />
                                        <SortableHeader label="Durum" sortKey="kalan_gun" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAlarmlar.map((a, i) => (
                                        <tr key={i} style={{ backgroundColor: a.alarm_durumu === 'gecikmis' ? '#fef2f2' : '#fffbeb' }}>
                                            <td style={tdStyle}>{a.firma_adi}</td>
                                            <td style={tdStyle}><strong>{a.seri_no}</strong></td>
                                            <td style={tdStyle}>{a.konum}</td>
                                            <td style={tdStyle}>{a.bir_sonraki_bakim_tarihi}</td>
                                            <td style={tdStyle}>
                                                {a.alarm_durumu === 'gecikmis' ? (
                                                    <span style={{ ...statusBadge, backgroundColor: '#fee2e2', color: '#dc2626' }}>
                                                        <AlertTriangle size={14} /> {Math.abs(a.kalan_gun)} gün geçti
                                                    </span>
                                                ) : (
                                                    <span style={{ ...statusBadge, backgroundColor: '#fef3c7', color: '#d97706' }}>
                                                        {a.kalan_gun} gün kaldı
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                            ✅ Şu an alarm gerektiren tüp bulunmuyor.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

const panelStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };
const summaryCard = { flex: 1, padding: '15px', borderRadius: '10px', border: '2px solid', textAlign: 'center', display: 'flex', flexDirection: 'column' };
const btnStyle = { color: 'white', border: 'none', borderRadius: '8px', padding: '10px 15px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' };
const iconBtnStyle = { background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '8px', cursor: 'pointer' };
const inputStyle = { padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' };
const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151' };
const thStyle = { padding: '12px', borderBottom: '2px solid #e5e7eb' };
const tdStyle = { padding: '12px', borderBottom: '1px solid #f3f4f6' };
const statusBadge = { padding: '4px 10px', borderRadius: '12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' };

export default AlarmSayfasi;
